import React, { useState } from 'react';
import {
  Card,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Space,
  Button,
  Alert,
  Tooltip,
  Spin,
  message,
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  FileTextOutlined,
  LinkOutlined,
  EyeOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { settingsApi, MultiServerStatisticsResponse, ServerStatisticsResult } from '../services/settingsApi';
import { useTheme } from '../contexts/ThemeContext';
import { theme } from 'antd';

const { Title, Text } = Typography;

const MultiServerStatistics: React.FC = () => {
  const { theme: currentTheme } = useTheme();
  const { token } = theme.useToken();
  const isDark = currentTheme === 'dark';
  const [autoRefresh, setAutoRefresh] = useState(false);

  const {
    data: statistics,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<MultiServerStatisticsResponse>(
    'multi-server-statistics',
    () => settingsApi.getMultiServerStatistics(),
    {
      refetchInterval: autoRefresh ? 30000 : false, // Auto refresh every 30s if enabled
      refetchOnWindowFocus: true,
      retry: 2,
      onError: (err: any) => {
        message.error(err.response?.data?.message || 'Failed to fetch statistics from servers');
      },
    }
  );

  // Error code colors and messages
  const getErrorTag = (result: ServerStatisticsResult) => {
    if (result.success) return null;

    const errorConfig: Record<string, { color: string; icon: React.ReactNode; message: string }> = {
      timeout: {
        color: 'orange',
        icon: <ClockCircleOutlined />,
        message: 'Timeout',
      },
      unauthorized: {
        color: 'red',
        icon: <CloseCircleOutlined />,
        message: 'Unauthorized',
      },
      server_error: {
        color: 'red',
        icon: <CloseCircleOutlined />,
        message: 'Server Error',
      },
      network_error: {
        color: 'volcano',
        icon: <CloseCircleOutlined />,
        message: 'Network Error',
      },
      invalid_response: {
        color: 'purple',
        icon: <CloseCircleOutlined />,
        message: 'Invalid Response',
      },
    };

    const config = errorConfig[result.errorCode || 'network_error'] || errorConfig.network_error;
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.message}
      </Tag>
    );
  };

  // Table columns
  const columns = [
    {
      title: 'Server',
      key: 'server',
      width: '25%',
      render: (_: any, record: ServerStatisticsResult) => (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: '15px' }}>
              {record.label || record.domain}
            </Text>
          </div>
          <Text
            type="secondary"
            style={{
              fontSize: '12px',
              fontFamily: 'monospace',
              display: 'block',
            }}
          >
            {record.domain}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: '12%',
      align: 'center' as const,
      render: (_: any, record: ServerStatisticsResult) => {
        if (record.success) {
          return (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              Online
            </Tag>
          );
        }
        return getErrorTag(record);
      },
    },
    {
      title: 'Total',
      dataIndex: ['data', 'total'],
      key: 'total',
      width: '10%',
      align: 'right' as const,
      render: (value: number, record: ServerStatisticsResult) => {
        if (!record.success || value === undefined) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Text strong style={{ fontSize: '16px' }}>
            {value.toLocaleString()}
          </Text>
        );
      },
    },
    {
      title: 'Indexed',
      dataIndex: ['data', 'indexed'],
      key: 'indexed',
      width: '10%',
      align: 'right' as const,
      render: (value: number, record: ServerStatisticsResult) => {
        if (!record.success || value === undefined) {
          return <Text type="secondary">-</Text>;
        }
        const percentage = record.data?.total ? ((value / record.data.total) * 100).toFixed(1) : '0';
        return (
          <div>
            <Text strong style={{ color: token.colorSuccess }}>
              {value.toLocaleString()}
            </Text>
            <div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {percentage}%
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Active',
      dataIndex: ['data', 'active'],
      key: 'active',
      width: '10%',
      align: 'right' as const,
      render: (value: number, record: ServerStatisticsResult) => {
        if (!record.success || value === undefined) {
          return <Text type="secondary">-</Text>;
        }
        const percentage = record.data?.total ? ((value / record.data.total) * 100).toFixed(1) : '0';
        return (
          <div>
            <Text strong style={{ color: token.colorInfo }}>
              {value.toLocaleString()}
            </Text>
            <div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {percentage}%
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Redirected',
      dataIndex: ['data', 'redirected'],
      key: 'redirected',
      width: '10%',
      align: 'right' as const,
      render: (value: number, record: ServerStatisticsResult) => {
        if (!record.success || value === undefined) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Text style={{ color: token.colorWarning }}>
            {value.toLocaleString()}
          </Text>
        );
      },
    },
    {
      title: 'Not Indexed',
      dataIndex: ['data', 'notIndexed'],
      key: 'notIndexed',
      width: '10%',
      align: 'right' as const,
      render: (value: number, record: ServerStatisticsResult) => {
        if (!record.success || value === undefined) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Text type="secondary">
            {value.toLocaleString()}
          </Text>
        );
      },
    },
    {
      title: 'Response Time',
      key: 'responseTime',
      width: '10%',
      align: 'right' as const,
      render: (_: any, record: ServerStatisticsResult) => {
        if (!record.responseTime) {
          return <Text type="secondary">-</Text>;
        }
        const time = record.responseTime;
        const color = time < 500 ? 'success' : time < 1000 ? 'warning' : 'error';
        return (
          <Tag color={color}>
            {time}ms
          </Tag>
        );
      },
    },
  ];

  const successfulServers = statistics?.servers?.filter(s => s.success) || [];
  const failedServers = statistics?.servers?.filter(s => !s.success) || [];

  return (
    <div>
      <div>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Space align="center" style={{ marginBottom: '16px' }}>
            <GlobalOutlined style={{ fontSize: '24px', color: token.colorPrimary }} />
            <Title level={2} style={{ margin: 0 }}>
              Global Statistics DirWeb
            </Title>
          </Space>
        </div>

        {/* Summary Cards */}
        {statistics ? (
          <Row gutter={[20, 20]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card
                styles={{
                  body: {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '24px',
                    color: 'white'
                  }
                }}
              >
                <Statistic
                  title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500 }}>Total Websites</span>}
                  value={statistics.total.total}
                  prefix={<DatabaseOutlined style={{ fontSize: '24px', color: 'rgba(255,255,255,0.9)' }} />}
                  valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card
                styles={{
                  body: {
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    padding: '24px',
                    color: 'white'
                  }
                }}
              >
                <Statistic
                  title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500 }}>Indexed</span>}
                  value={statistics.total.indexed}
                  prefix={<FileTextOutlined style={{ fontSize: '24px', color: 'rgba(255,255,255,0.9)' }} />}
                  valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: 700 }}
                  suffix={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', marginLeft: '8px' }}>({statistics.total.total > 0 ? ((statistics.total.indexed / statistics.total.total) * 100).toFixed(1) : 0}%)</span>}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card
                styles={{
                  body: {
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    padding: '24px',
                    color: 'white'
                  }
                }}
              >
                <Statistic
                  title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500 }}>Active</span>}
                  value={statistics.total.active}
                  prefix={<EyeOutlined style={{ fontSize: '24px', color: 'rgba(255,255,255,0.9)' }} />}
                  valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: 700 }}
                  suffix={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', marginLeft: '8px' }}>({statistics.total.total > 0 ? ((statistics.total.active / statistics.total.total) * 100).toFixed(1) : 0}%)</span>}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card
                styles={{
                  body: {
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    padding: '24px',
                    color: 'white'
                  }
                }}
              >
                <Statistic
                  title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500 }}>Redirected</span>}
                  value={statistics.total.redirected}
                  prefix={<LinkOutlined style={{ fontSize: '24px', color: 'rgba(255,255,255,0.9)' }} />}
                  valueStyle={{ color: '#fff', fontSize: '32px', fontWeight: 700 }}
                />
              </Card>
            </Col>
          </Row>
        ) : null}

        {/* Status Alert */}
        {statistics ? (
          <Alert
            message={
              <div>
                <Text strong>{successfulServers.length}</Text> servers online
                {failedServers.length > 0 && (
                  <span> • <Text type="danger"><Text strong>{failedServers.length}</Text> servers offline</Text></span>
                )}
                {statistics.totalResponseTime && (
                  <span> • <Text type="secondary">Fetched in {statistics.totalResponseTime}ms</Text></span>
                )}
                {statistics.timestamp && (
                  <span> • <Text type="secondary">Last updated: {new Date(statistics.timestamp).toLocaleString()}</Text></span>
                )}
              </div>
            }
            type={failedServers.length === 0 ? 'success' : 'warning'}
            showIcon
            style={{ marginBottom: '24px' }}
            action={
              <Button
                size="small"
                type={autoRefresh ? 'primary' : 'default'}
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? 'Auto Refresh ON' : 'Auto Refresh OFF'}
              </Button>
            }
          />
        ) : null}

        {/* Error Alert */}
        {error ? (
          <Alert
            message="Failed to fetch statistics"
            description={error instanceof Error ? error.message : 'Unknown error occurred'}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
            action={
              <Button size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : null}

        {/* Main Table */}
        <Card
          title={
            <Space>
              <GlobalOutlined />
              <span>Server Statistics</span>
              <Tag>{statistics?.servers?.length || 0} servers</Tag>
            </Space>
          }
          extra={
            <Space>
              <Tooltip title="Refresh data">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  loading={isFetching}
                >
                  Refresh
                </Button>
              </Tooltip>
            </Space>
          }
          style={{
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: '8px',
          }}
        >
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text type="secondary">Loading statistics from all servers...</Text>
              </div>
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={statistics?.servers || []}
              rowKey="serverId"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} servers`,
              }}
              rowClassName={(record) => {
                if (!record.success) {
                  return '';
                }
                return '';
              }}
              onRow={(record) => {
                if (!record.success) {
                  return {
                    style: {
                      backgroundColor: isDark ? '#2a1215' : '#fff2f0',
                    },
                    onMouseEnter: (e) => {
                      e.currentTarget.style.backgroundColor = isDark ? '#3a1a1d' : '#ffe7e5';
                    },
                    onMouseLeave: (e) => {
                      e.currentTarget.style.backgroundColor = isDark ? '#2a1215' : '#fff2f0';
                    },
                  };
                }
                return {};
              }}
              style={{
                background: isDark ? '#1f1f1f' : '#fff',
              }}
            />
          )}
        </Card>

        {/* Error Details */}
        {failedServers.length > 0 ? (
          <Card
            title="Failed Servers Details"
            style={{ marginTop: '24px' }}
            type="inner"
          >
            <div>
              {failedServers.map((server) => (
                <Alert
                  key={server.serverId}
                  message={
                    <div>
                      <Text strong>{server.label || server.domain}</Text>
                      <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: '12px', marginLeft: '8px' }}>
                        {server.domain}
                      </Text>
                    </div>
                  }
                  description={
                    <div>
                      <Text type="danger">{server.error}</Text>
                      {server.responseTime ? (
                        <div style={{ marginTop: '4px' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Response time: {server.responseTime}ms
                          </Text>
                        </div>
                      ) : null}
                    </div>
                  }
                  type="error"
                  showIcon
                  style={{ marginBottom: '8px' }}
                />
              ))}
            </div>
          </Card>
        ) : null}
      </div>
    </div>
  );
};

export default MultiServerStatistics;

